import { Directive, ElementRef, HostListener, Input, OnDestroy, ViewContainerRef } from '@angular/core'
import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { merge, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { SosListComponent } from './sos-list/sos-list.component'

@Directive({
  selector: '[linSosTriggerFor]',
  standalone: false,
})
export class SosTriggerDirective implements OnDestroy {
  private overlayRef: OverlayRef
  private portal: TemplatePortal
  private readonly destroy$ = new Subject<void>()

  @Input('linSosTriggerFor') listComponent: SosListComponent

  @HostListener('click')
  onClick(): void {
    this.openList()
  }

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly hostElement: ElementRef,
  ) {}

  openList(): void {
    if (this.overlayRef?.hasAttached()) return
    // detach() leaves the pane in the DOM; dispose the previous ref before re-opening to avoid a leak
    this.overlayRef?.dispose()
    this.overlayRef = this.overlay.create(this.getOverlayConfig())
    this.portal = new TemplatePortal(this.listComponent.templateRef, this.viewContainerRef)
    this.overlayRef.attach(this.portal)

    merge(this.overlayRef.backdropClick(), this.listComponent.closed)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.overlayRef.detach()
      })
  }

  private getOverlayConfig(): OverlayConfig {
    const belowPosition: ConnectedPosition = {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    }

    return new OverlayConfig({
      positionStrategy: this.overlay.position().flexibleConnectedTo(this.hostElement).withPositions([belowPosition]),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    })
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose()
    this.destroy$.next()
    this.destroy$.complete()
  }
}
